/*
===============================================================================================================
                                 Copyright 2024-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import { getRuleActionValue, RuleTypes } from './rules';
import { isEmpty } from './validation';

const triggerEventMappings = {
  'RETURN_EVENT-ACCEPTED': {
    name: 'refunds.triggerTypes.returnBooked.name',
    description: 'refunds.triggerTypes.returnBooked.description',
    telemetryEvent: 'Merchant_DefaultTrigger_Booked_Selected',
  },
  'RETURN_EVENT-DROPPED_OFF': {
    name: 'refunds.triggerTypes.returnDroppedOff.name',
    description: 'refunds.triggerTypes.returnDroppedOff.description',
    telemetryEvent: 'Merchant_DefaultTrigger_DroppedOff_Selected',
  },
  'RETURN_EVENT-ON_ITS_WAY': {
    name: 'refunds.triggerTypes.returnOnItsWay.name',
    description: 'refunds.triggerTypes.returnOnItsWay.description',
    telemetryEvent: 'Merchant_DefaultTrigger_OnItsWay_Selected',
  },
  'RETURN_EVENT-ARRIVED_AT_DESTINATION': {
    name: 'refunds.triggerTypes.returnDelivered.name',
    description: 'refunds.triggerTypes.returnDelivered.description',
    telemetryEvent: 'Merchant_DefaultTrigger_Delivered_Selected',
  },
  'RETURN_EVENT-PROCESSED_AT_FACILITY': {
    name: 'refunds.triggerTypes.returnProcessed.name',
    description: 'refunds.triggerTypes.returnProcessed.description',
    telemetryEvent: 'Merchant_DefaultTrigger_Processed_Selected',
  },
};

export const DEFAULT_FEE_VALUES = {
  name: 'Default fee',
  amount: 0,
};

export const REFUND_METHODS = {
  STANDARD_REFUND: 'STANDARD_REFUND',
  STORE_CREDIT: 'STORE_CREDIT',
};

/**
 * Checks whether the Gift Returns feature should be enabled.
 * @param {object} options
 * @param {boolean} options.giftReturnsFeatureEnabled
 * @param {Array}   options.refundMethodRules
 * @return {boolean}
 */
export const isGiftReturnsEnabled = ({ giftReturnsFeatureEnabled, refundMethodRules = [] }) => {
  if (!giftReturnsFeatureEnabled) return false;
  const defaultRule = refundMethodRules.find((rule) => rule.isDefault);
  const refundMethods = defaultRule?.rule?.event?.params?.actionValue?.refundMethods || [];
  return refundMethods.includes(REFUND_METHODS.STORE_CREDIT);
};

/**
* Generates dropdown options for refund triggers
* @param {object} options
* @param {string[]} options.merchantWebhooks list of all return webhook events the merchant is subscribed to
* @param {string[]} options.supportedWebhooks list of webhook events that support refunds, driven by the config
* @param {() => string} options.translate i18n translate function
* @return {{ title: string, value: string }[]} A list of dropdown options
 */
export const getRefundTriggersOptions = ({
  merchantWebhooks = [],
  supportedWebhooks = [],
  translate,
}) => {
  const filteredTriggers = supportedWebhooks.filter((eventType) => merchantWebhooks.includes(eventType));

  if (isEmpty(filteredTriggers)) return [];

  const options = filteredTriggers.map((eventType) => ({
    title: triggerEventMappings[eventType]?.name ? translate(triggerEventMappings[eventType].name) : 'Invalid key',
    value: eventType,
  }));
  return options;
};

/**
* Getter for a refund trigger description locale
* @param {string} event Refund trigger event name
* @return {string} Locale string
 */
export const getRefundTriggerDescription = (event) => triggerEventMappings[event]?.description || '';

/**
* Getter for a refund trigger telemetry event
* @param {string} trigger Refund trigger name
* @return {string} Event name string
 */
export const getRefundTriggerTelemetryEvent = (trigger) => triggerEventMappings[trigger].telemetryEvent ?? '';

/**
* Getter for a refund trigger name locale
* @param {string} event Refund trigger event
* @return {string} Locale string
 */
export const getRefundTriggerName = (event) => triggerEventMappings[event]?.name || '';

export const generateFeeRulePayload = ({ existingRule, defaultCurrency, currencyOverride }) => {
  // default fee rule payload
  let rulePayload = {
    name: 'Default fee rule',
    conditions: [{
      fact: 'product',
      operator: 'default',
      value: ['true'],
    }],
    isDefault: true,
    ruleType: RuleTypes.REFUND_FEES,
    eventData: {
      amount: 0,
      name: 'Default fee',
      currency: currencyOverride ?? defaultCurrency,
    },
  };

  if (existingRule) {
    const { amount, name, currency } = getRuleActionValue({ rule: existingRule });
    rulePayload = {
      ...rulePayload,
      name: existingRule.ruleDescription,
      conditions: existingRule.rule.conditions.all,
      ruleId: existingRule.ruleId,
      enabled: existingRule.enabled,
      eventData: {
        amount,
        name,
        currency: currencyOverride ?? currency,
      },
    };
  }
  return rulePayload;
};

export default {
  getRefundTriggersOptions,
  getRefundTriggerDescription,
  generateFeeRulePayload,
};
